import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { CSSTransitionGroup } from 'react-transition-group';
import throttle from 'lodash/throttle';

import { onPageEditing } from 'utils';

import Button from 'components/Button';
import ContactBlock from 'components/ContactBlock';
import Icon from 'components/Icon';
import ImageIcon from 'components/ImageIcon';
import ImageBlock from 'components/ImageBlock';
import VideoListBlock from 'components/VideoListBlock';
import Infographic from 'components/Infographic';
import InfographicListBlock from 'components/InfographicListBlock';

import { track } from 'tracking';

import css from './TabListBlock.module.scss';

class TabListBlock extends React.Component {
    static propTypes = {
        blockId: PropTypes.number.isRequired,
        firstOpen: PropTypes.bool.isRequired,
        heading: PropTypes.string,
        intro: PropTypes.string,
        tabs: PropTypes.arrayOf(PropTypes.shape({
            blockId: PropTypes.number,
            heading: PropTypes.string.isRequired,
            title: PropTypes.string,
            icon: PropTypes.string,
            intro: PropTypes.string,
            text: PropTypes.string,
            content: PropTypes.array,
            contentType: PropTypes.string,
            link: PropTypes.string,
            linkText: PropTypes.string,
        })).isRequired,
        readMoreLabel: PropTypes.string.isRequired,
        contentType: PropTypes.string,
    }

    state = {
        active: this.props.firstOpen ? 0 : null,
        tabIndicatorLeft: null,
        tabIndicatorWidth: null,
    }

    _checkIfPageWithScroll = () => {
        if (window.innerWidth > document.body.clientWidth) {
            document.body.classList.add("pageWithScroll");
        }else{
            document.body.classList.remove("pageWithScroll");
        }
    };
 

    componentDidMount() {
        setTimeout(()=>{
            this._setActiveIndicatorStyle(this.state.active);
        },1);
        
        this._checkIfPageWithScroll();
                
        window.addEventListener('resize',() => {    
            this._handleResize();
            this._checkIfPageWithScroll();  
       });  
    }

    _handleResize = throttle(() => {
        this._setActiveIndicatorStyle(this.state.active);
    }, 100);

    _setActiveIndicatorStyle = tabId => {
        if(! this.navTabs[tabId]) return;

        this.setState({
            tabIndicatorLeft: this.navTabs[tabId].offsetLeft - this.list.scrollLeft,
            tabIndicatorWidth: this.navTabs[tabId].offsetWidth,
        });
    }

    _handleBenefitClick = e => {
        e.preventDefault();
        const { id } = e.currentTarget.dataset;

        if(this.state.active === parseInt(id)) {
            // This "tab" is already opened, close the list
            this.setState({ active: null });
        } else {
            const active = parseInt(id);
            const tab = this.props.tabs[active];

            // GA Tracking
            track('tab_selection', `${this.props.heading || ""} - ${tab.title || tab.heading}`);

            this.setState({ active });
        }

        this._setActiveIndicatorStyle(id);
    }

    _renderBlock(content, index) {
        if(content.length <= 0){
            return null;
        }
        const block = content[0];
        const contentType = block.contentType;
        switch(contentType) {
            case 'ContactPersonListBlock':
                return <ContactBlock {...block} key={index} />;
            case 'ContactPersonBlock':
                return <ContactBlock contacts={[block]} key={index} />;

            case 'VideoListBlock':
                return <VideoListBlock {...block} key={index} />;
            case 'YoutubeVideoBlock':
                return <VideoListBlock videos={[block]} key={index} blockId={block.blockId} />;

            case 'InfographicListBlock':
                return <InfographicListBlock {...block} key={index} />;
            case 'InfographicBlock':
                return <Infographic {...block} key={index} />;

            case 'ImageListBlock':
                return <ImageBlock {...block} key={index} />;
            case 'ImageBlock':
                return <ImageBlock images={[block]} key={index} />;
        }
    }

    _onPageEdit(type, property) {
        if(type === 'tab' && this.props.contentType !== 'TabListBlock') {
            return onPageEditing(property);
        } else if(type === 'list' && this.props.contentType === 'TabListBlock') {
            return onPageEditing(property);
        }

        return null;
    }

    navTabs = {};

    render() {
        const {
            blockId,
            heading,
            intro,
            tabs,
            readMoreLabel,
        } = this.props;

        const {
            active,
            tabIndicatorLeft,
            tabIndicatorWidth,
        } = this.state;

        let tabsWithIcon = tabs.filter(x => x.iconNew || x.icon);
        let noItemsWithIcons = tabsWithIcon.length === 0 ? true: false;

        return (
            <div className={css.block} data-block={blockId}>
                <header className={css.header}>
                    <h2
                        className="block-title-large"
                        {...this._onPageEdit("list", "Heading")}
                    >{heading}</h2>
                    {intro && <p className="ingress" {...this._onPageEdit("list", "Intro")}>{intro}</p>}
                    {tabs && (
                        <nav {...this._onPageEdit("list", "Tabs") } onScroll={this._handleResize}>
                            <ul ref={node => this.list = node} className={noItemsWithIcons ? css.noIcons: null}>
                                {tabs.map((b, index) => (
                                    <li
                                        className={css.tab}
                                        key={index}
                                    >
                                        <span className={css.tabInner}>
                                            <button
                                                ref={b => this.navTabs[index] = b}
                                                className={cn(css.button, {
                                                    [css.active]: index == active
                                                })}
                                                data-id={index}
                                                onClick={this._handleBenefitClick}
                                                type="button"
                                            >
                                                <span className={css.iconWrapper}>
                                                    {b.iconNew ? (
                                                        <ImageIcon {...b.iconNew} />
                                                    ) : 
                                                        (b.icon ? (
                                                            <Icon icon={b.icon} />
                                                        ) : null)
                                                    }
                                                </span>
                                                <span>{b.title||b.heading}</span>
                                            </button>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            {active !== null && !!tabIndicatorLeft && !!tabIndicatorWidth &&
                                <div
                                    className={css["active-tab-indicator"]}
                                    style={{
                                        transform: `translateX(${tabIndicatorLeft}px)`,
                                        width: this.state.tabIndicatorWidth,
                                    }}
                                />
                            }
                        </nav>
                    )}
                </header>
                <CSSTransitionGroup
                    className={css["tabs-wrapper"]}
                    transitionName={{
                        enter: css["enter"],
                        enterActive: css["enter-active"],
                        leave: css["leave"],
                        leaveActive: css["leave-active"]
                    }}
                    transitionEnterTimeout={300}
                    transitionLeaveTimeout={300}
                >
                    {tabs && tabs.map((tab, index) => {
                        return active !== index ? null : (
                            <div key={index} className={cn(css.wrapper, {
                                [css.noSide]: !tab.content || tab.content.length === 0
                            })}>
                                {tab.content && tab.content.length > 0 && (
                                    <div className={css.side} {...this._onPageEdit("tab", "Content")}>
                                        {this._renderBlock.apply(this, [tab.content, index])}
                                    </div>
                                )}
                                <div className={css.content}>
                                    <h3 className="h2" {...this._onPageEdit("tab", "Heading")}>{tab.heading||tab.title}</h3>
                                    <p
                                        className={css.intro}
                                        dangerouslySetInnerHTML={{__html: tab.intro}}
                                        {...this._onPageEdit("tab", "Intro")}/>
                                    <div
                                        className={css.text}
                                        dangerouslySetInnerHTML={{__html: tab.text}}
                                        {...this._onPageEdit("tab", "text")}
                                    />
                                    {tab.link && (
                                        <p className={css.readMore} {...this._onPageEdit("tab", "ReadMore")}>
                                            <Button secondary href={tab.link}>{tab.linkText ? tab.linkText : readMoreLabel}</Button>
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </CSSTransitionGroup>
            </div>
        );
    }
}

export default TabListBlock;



// WEBPACK FOOTER //
// ./src/components/TabListBlock/TabListBlock.jsx